import { useCallback } from 'react';
import { Configuration } from '@azure/msal-browser';
import { useMsal } from '@azure/msal-react';

import { useApiAuth } from './auth/ApiAuthProvider';
import { accountKey } from './auth/apiSession';

const browserOrigin =
  globalThis.window === undefined ? '/' : globalThis.location.origin;

// Config object to be passed to Msal on creation
export const msalConfig: Configuration = {
  auth: {
    clientId: process.env.VITE_AZURE_AD_CLIENTID || 'Unknown AD:ClientID',
    authority: process.env.VITE_AZURE_AD_AUTHORITY || 'Unknown AD:Authority',
    redirectUri: process.env.VITE_AZURE_AD_REDIRECT_URI || browserOrigin,
    postLogoutRedirectUri:
      process.env.VITE_AZURE_AD_POST_LOGOUT_REDIRECT_URI || browserOrigin,
    navigateToLoginRequestUrl: true,
  },
  cache: {
    // Keep the signed-in account available after a full-page navigation or a
    // server rewrite back to index.html.
    cacheLocation: 'localStorage',
    storeAuthStateInCookie: true,
  },
  system: {
    allowNativeBroker: false, // Disables WAM Broker
  },
};

export type Permissions = 'All' | 'GC.Reader' | 'GC.Writer' | 'BI.Reader';

export const apiScopes = [`api://${process.env.VITE_AZURE_AD_CLIENTID}/Access`];

export function useAccessToken() {
  const { instance, accounts } = useMsal();
  const { token, error, accountId } = useApiAuth();
  const account = instance.getActiveAccount() ?? accounts[0];

  const hasAccessByRole = useCallback(
    (permission: Permissions | Permissions[]) => {
      const roles = account?.idTokenClaims?.roles ?? [];

      if (roles.includes('All')) {
        return true;
      }

      return Array.isArray(permission)
        ? permission.some((p) => roles.includes(p))
        : roles.includes(permission);
    },
    [account],
  );

  return {
    token: accountId === accountKey(account ?? null) ? token : null,
    error,
    roles: account?.idTokenClaims?.roles ?? [],
    hasAccessByRole,
  };
}
